import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import {
  ROUTE_ABOUT_US,
  ROUTE_ALL_ENQUIRIES,
  ROUTE_TASKS,
  ROUTE_CONTACT_US,
  ROUTE_DELETE_ACCOUNT,
  ROUTE_HOME,
  ROUTE_INVOICE_HISTORY,
  ROUTE_PRIVACY_POLICY,
  ROUTE_PROFILE,
  ROUTE_RATE_US,
  ROUTE_REFUND_POLICY,
  ROUTE_ROLES,
  ROUTE_TEAM,
  ROUTE_TERMS_AND_CONDITIONS,
  ROUTE_FEEDBACK,
  ROUTE_VISITOR_LOG,
  ROUTE_TUTORIALS,
  ROUTE_MAIN_HOME,
  ROUTE_DASHBOARD,
  MENU_PERMISSIONS,
  MENU_HOME,
  MENU_CHECK_AVAILABILITY,
  MENU_ALL_ENQUIRIES,
  MENU_PROFILE,
  MENU_INVOICE_HISTORY,
  MENU_DASHBOARD,
  MENU_ROLES,
  MENU_TEAM,
  MENU_TASKS_CHECKLIST,
  MENU_FEEDBACK,
  MENU_REGISTERBOOK,
  MENU_TUTORIALS,
  SESSION_USER_TYPE_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from "../Util/VMConstants";
import { checkIsMenuPermissionExist, isAdminLoggedIn } from "../Util/UtilHelper";
import StorageFactory from "../Util/StorageFactory";

/** Keep in sync with `App.js` shell — mismatched widths make the sidebar overlap the outlet (e.g. DataGrid under the drawer). */
export const DRAWER_WIDTH = 250;
export const LAYOUT_NAVBAR_HEIGHT = 65;

export default function SidebarDrawer({ toggleDrawer }) {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sessionFactory = new StorageFactory("session");
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  
  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  // ✅ Menu config WITH permissions
  const mainMenuItems = [
    {
      title: MENU_HOME,
      path: ROUTE_MAIN_HOME,
      icon: "/static/home.svg",
      permission: MENU_PERMISSIONS.MENU_HOME,
    },
    {
      title: MENU_CHECK_AVAILABILITY,
      path: ROUTE_HOME,
      icon: "/static/calender.svg",
      permission: MENU_PERMISSIONS.MENU_CHECK_AVAILABILITY,
    },
    {
      title: MENU_ALL_ENQUIRIES,
      path: ROUTE_ALL_ENQUIRIES,
      icon: "/static/calendar-check.svg",
      permission: MENU_PERMISSIONS.MENU_ALL_ENQUIRIES,
    },
    {
      title: MENU_PROFILE,
      path: ROUTE_PROFILE,
      icon: "/static/user-circle.svg",
      permission: MENU_PERMISSIONS.MENU_PROFILE,
    },
    {
      title: MENU_INVOICE_HISTORY,
      path: ROUTE_INVOICE_HISTORY,
      icon: "/static/calendar-dollar.svg",
      permission: MENU_PERMISSIONS.MENU_INVOICE_HISTORY,
    },
    {
      title: MENU_DASHBOARD,
      path: ROUTE_DASHBOARD,
      icon: "/static/report.svg",
      permission: MENU_PERMISSIONS.MENU_DASHBOARD,
    },
    {
      title: MENU_ROLES ,
      path: ROUTE_ROLES,
      icon: "/static/hierarchy.svg",
      permission: MENU_PERMISSIONS.MENU_ROLES,
      adminOnly: true,
    },
    {
      title: MENU_TEAM,
      path: ROUTE_TEAM,
      icon: "/static/users-group.svg",
      permission: MENU_PERMISSIONS.MENU_TEAM,
      adminOnly: true,
    },
    // {
    //   title: MENU_CONFIGURATION,
    //   path: ROUTE_CONFIGURATION,
    //   icon: "/static/settings.svg",
    //   permission: MENU_PERMISSIONS.MENU_CONFIGURATION,
    // },
    {
      title: MENU_TASKS_CHECKLIST,
      path: ROUTE_TASKS,
      icon: "/static/taskcheck.png",
      permission: MENU_PERMISSIONS.MENU_TASKS_CHECKLIST,
    },
    {
      title: MENU_FEEDBACK,
      path: ROUTE_FEEDBACK,
      icon: "/static/feedback.svg",
      permission: MENU_PERMISSIONS.MENU_FEEDBACK,
    },
    {
      title: MENU_REGISTERBOOK,
      path: ROUTE_VISITOR_LOG,
      icon: "/static/visitor.png",
      permission: MENU_PERMISSIONS.MENU_REGISTERBOOK,
    },
    {
      title: MENU_TUTORIALS,
      path: ROUTE_TUTORIALS,
      icon: "/static/vm-howtouse.svg",
      alwaysVisible: true,
    },
  ];

  const settingsSubMenu = [
    { title: "About Us", path: ROUTE_ABOUT_US, icon: "/static/vm-aboutus.svg" },
    { title: "Terms & Conditions", path: ROUTE_TERMS_AND_CONDITIONS, icon: "/static/vm-temsandcond.svg" },
    { title: "Privacy Policy", path: ROUTE_PRIVACY_POLICY, icon: "/static/vm-privacy.svg" },
    { title: "Refund Policy", path: ROUTE_REFUND_POLICY, icon: "/static/vm-refund.svg" },
    // { title: "How To Use", path: ROUTE_HOW_TO_USE, icon: "/static/vm-howtouse.svg" },
    { title: "Contact Us", path: ROUTE_CONTACT_US, icon: "/static/vm-contactus.svg" },
    { title: "Delete Account", path: ROUTE_DELETE_ACCOUNT, icon: "/static/vm-delete.svg", adminOnly: true },
    { title: "Rate Us", path: ROUTE_RATE_US, icon: "/static/vm-rateus.svg" },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "#f8f9fa",
          borderRight: "1px solid #e0e0e0",
          marginTop: `${LAYOUT_NAVBAR_HEIGHT}px`,
          height: `calc(100vh - ${LAYOUT_NAVBAR_HEIGHT}px)`,
          overflowY: "auto",
        },
      }}
    >
      <List>
        {mainMenuItems
          .filter((item) => {
            if (item.alwaysVisible) return true;
            if (item.adminOnly) return isAdminLoggedIn(userType);
            return (
              isAdminLoggedIn(userType) ||
              checkIsMenuPermissionExist(VM_PERMISSIONS, item.permission)
            );
          })
          .map((item) => {
            const isActive = location.pathname === `/${item.path}`;

            return (
              <Link
                key={item.title}
                to={`/${item.path}`}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={() => {
                  setSettingsOpen(false);
                  if (isMobile) toggleDrawer();
                }}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{
                      py: 1.5,
                      px: 3,
                      backgroundColor: isActive ? "#e3f2fd" : "transparent",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <img src={item.icon} alt={item.title} style={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          fontWeight={isActive ? "bold" : "medium"}
                          color={isActive ? "primary" : "textPrimary"}
                        >
                          {item.title}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </Link>
            );
          })}

        {/* SETTINGS — admin only */}
        {isAdminLoggedIn(userType) && (
          <>
        <ListItem disablePadding>
          <ListItemButton 
          onClick={handleSettingsToggle} 
          sx={{
              py: 1.5,
              px: 3,
              "&:hover": {
                backgroundColor: "#e3f2fd",
              },
            }} 
            >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <img src="/static/settings.svg" alt="Settings" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
             <ListItemText
              primary={<Typography variant="body1" fontWeight="medium">Settings</Typography>}
            />
            {settingsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {settingsSubMenu.map((subItem, index) => (
              <Link
                to={subItem.path}
                key={index}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={() => {
                  if (isMobile) toggleDrawer(); // Close drawer on sub-item click in mobile
                }}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{
                      pl: 6,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: "#f1f1f1",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <img
                        src={subItem.icon}
                        alt={subItem.title}
                        style={{ width: 20, height: 20 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{subItem.title}</Typography>}
                    />
                  </ListItemButton>
                </ListItem>
              </Link>
            ))}
          </List>
        </Collapse>
          </>
        )}
      </List>
    </Drawer>
  );
}
